import { Divider, Grid2, keyframes } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../context/AppContextProvider';
import {
  useConfigDvrChannelsAsync,
  useGetDvrChannelsAsync,
} from '../../../../services/Devices/DvrService/hooks/useMutation';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DvrChannelsConfigFormSchema } from '../../../../services/Devices/DvrService/schemas';
import zod, { undefined } from 'zod';
import {
  IDvrChannelEditionProps,
  IDvrChannelProps,
} from '../../../../services/Devices/DvrService/types';
import {
  Add,
  AddLink,
  Check,
  Close,
  Link,
  LinkOff,
  Remove,
} from '@mui/icons-material';
import ButtonComponent from '../../../../components/ButtonComponent';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import { useGetAllDevicesByLicenseAsync } from '../../../../services/Devices/hooks/useMutation';
import { IDeviceSimpleProps } from '../../../../services/Devices/types';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../../components/DropSelectComponent';

interface IDvrChannelsConfigTabProps {
  selectedDevice: any;
}

type DvrChannelsConfigFormInput = zod.infer<typeof DvrChannelsConfigFormSchema>;

const blink = keyframes`
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
    100% {
        opacity: 1;
    }
`;

const DvrChannelsConfigTab = ({
  selectedDevice,
}: IDvrChannelsConfigTabProps) => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { control, reset, watch, handleSubmit, getValues } =
    useForm<DvrChannelsConfigFormInput>({
      resolver: zodResolver(DvrChannelsConfigFormSchema),
      defaultValues: {
        channels: [],
      },
    });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'channels',
    keyName: 'key',
  });
  const channels = watch('channels');
  const [dvrChannels, setDvrChannels] = useState<IDvrChannelProps[]>([]);
  const [deviceOptions, setDeviceOptions] = useState<IDeviceSimpleProps[]>([]);
  const [editionSnapshot, setEditionSnapshot] = useState<
    IDvrChannelEditionProps[]
  >([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { mutateAsync: getChannels } = useGetDvrChannelsAsync();
  const { mutateAsync: configChannels } = useConfigDvrChannelsAsync();
  const { mutateAsync: getDeviceOptions } = useGetAllDevicesByLicenseAsync();
  const [key, setKey] = useState<number>(0);

  const getDeviceSelectionOptions = (): IDropSelectOptions[] => {
    return deviceOptions.map((i) => {
      return {
        option: i.name,
        value: i.id,
      } as IDropSelectOptions;
    });
  };

  const renderItemByChannel = (channel: IDvrChannelEditionProps) => {
    const deviceName = dvrChannels.find(
      (i) => i.device?.id === channel.deviceId,
    )?.device?.name;
    const channelNum =
      typeof channel.channel === 'number' ? channel.channel + 1 : 0;

    if (!deviceName) {
      return (
        <Grid2 container alignItems={'center'} gap={1}>
          <Grid2>
            <span>Canal: {channelNum}</span>
          </Grid2>
          <LinkOff
            color={'error'}
            sx={{ mx: 0.5, width: '28px', height: '28px' }}
          />
          <Grid2>
            <span>Dispositivo: Não vinculado</span>
          </Grid2>
        </Grid2>
      );
    }

    return (
      <Grid2 container alignItems={'center'} gap={1}>
        <Grid2>
          <span>Canal: {channelNum}</span>
        </Grid2>
        <Link
          color={'success'}
          sx={{ mx: 0.5, width: '28px', height: '28px' }}
        />
        <Grid2>
          <span>Dispositivo: {deviceName}</span>
        </Grid2>
      </Grid2>
    );
  };

  const renderEditionItemByChannel = (idx: number) => {
    const isDeviceId = !!channels[idx].deviceId;
    return (
      <Grid2 container alignItems={'center'} gap={1}>
        <Grid2>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                key={key}
                label="Canal"
                onChangeText={(val) => {
                  const num = Number(val) - 1;
                  onChange(num);
                }}
                type={'number'}
                value={value + 1}
                sx={{ marginBottom: -2 }}
                size={'small'}
                errorMessage={error?.message}
              />
            )}
            name={`channels.${idx}.channel`}
            control={control}
          />
        </Grid2>
        <Grid2>
          {isDeviceId ? (
            <Link
              color={'secondary'}
              sx={{
                mx: 0.5,
                width: '28px',
                height: '28px',
              }}
            />
          ) : (
            <AddLink
              color={'primary'}
              sx={{
                mx: 0.5,
                width: '28px',
                height: '28px',
                animation: `${blink} 1s infinite`,
              }}
            />
          )}
        </Grid2>
        <Grid2>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                key={key}
                label={'Dispositivo'}
                valueSelected={value}
                onChange={onChange}
                options={getDeviceSelectionOptions()}
                size={'small'}
                isRequired={false}
                isError={!!error}
                errorMessage={error?.message}
              />
            )}
            name={`channels.${idx}.deviceId`}
            control={control}
          />
        </Grid2>
        <Grid2 sx={{ flexGrow: 1 }} />
        <Grid2>
          <ButtonComponent
            variant={'outlined'}
            color={'error'}
            size={'small'}
            onClick={() => removeChannelItem(idx)}
          >
            <Remove sx={{ mr: '4px', width: '20px', height: '20px' }} />
            <span>Remover</span>
          </ButtonComponent>
        </Grid2>
      </Grid2>
    );
  };

  const renderEditionButtons = () => {
    if (isEditing) {
      return (
        <Grid2 container alignItems={'center'} gap={2}>
          <Grid2>
            <ButtonComponent
              variant={'outlined'}
              color={'success'}
              size={'medium'}
              onClick={addChannelItem}
            >
              <Add sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Adicionar</span>
            </ButtonComponent>
          </Grid2>
          <Grid2 sx={{ flexGrow: 1 }} />
          <Grid2>
            <ButtonComponent
              color={'primary'}
              size={'medium'}
              onClick={handleSubmit(saveEdition)}
            >
              <Check sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Salvar</span>
            </ButtonComponent>
          </Grid2>
          <Grid2>
            <ButtonComponent
              color={'error'}
              size={'medium'}
              onClick={cancelEditionMode}
            >
              <Close sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Cancelar</span>
            </ButtonComponent>
          </Grid2>
        </Grid2>
      );
    }

    return (
      <Grid2 container alignItems={'center'}>
        <ButtonComponent
          color={'secondary'}
          size={'medium'}
          onClick={startEditionMode}
        >
          Editar
        </ButtonComponent>
      </Grid2>
    );
  };

  const addChannelItem = () => {
    if (fields.length === 0) {
      append({ channel: 0 });
    } else {
      const lastItem = fields[fields.length - 1].channel;
      append({ channel: lastItem + 1 });
    }
  };

  const removeChannelItem = (idx: number) => {
    remove(idx);
  };

  const startEditionMode = () => {
    setEditionSnapshot(fields);
    setIsEditing(true);
  };

  const endEditionMode = async () => {
    await fetchChannels();
    setEditionSnapshot([]);
    setIsEditing(false);
  };

  const cancelEditionMode = () => {
    setIsEditing(false);
    if (editionSnapshot && editionSnapshot.length > 0) {
      reset({ channels: editionSnapshot }, { keepDefaultValues: true });
    }
    setEditionSnapshot([]);
  };

  const saveEdition = async (data: DvrChannelsConfigFormInput) => {
    setIsShowLoadingComponent(true);
    await configChannels({ id: selectedDevice.id, data })
      .then(() => {
        showSnackbar('Configuração de canais efetuada com sucesso!', 'success');
        endEditionMode();
      })
      .catch((error) => {
        cancelEditionMode();
      })
      .finally(() => setIsShowLoadingComponent(false));
  };

  const fetchDeviceOptions = useCallback(async () => {
    if (!deviceOptions || deviceOptions?.length === 0) {
      await getDeviceOptions({}).then((response) => {
        const data = response.filter((i) => i.type != 'DVR');
        setDeviceOptions(data);
      });
    }
  }, [deviceOptions]);

  const fetchChannels = useCallback(async () => {
    await getChannels(selectedDevice.id).then((response) => {
      setDvrChannels(response);

      const channelsData: IDvrChannelEditionProps[] = response.map((i) => ({
        id: i.id,
        channel: i.channel,
        deviceId: i.device?.id ?? undefined,
      }));

      reset({ channels: channelsData }, { keepDefaultValues: true });
    });
  }, [getChannels, reset, selectedDevice]);

  useEffect(() => {
    if (selectedDevice && fields.length === 0) {
      fetchChannels();
      fetchDeviceOptions();
    }
  }, [selectedDevice]);

  useEffect(() => {
    setKey(Math.random() * 10);
  }, []);

  return (
    <Grid2
      container
      direction="column"
      size={{ md: 12, lg: 8 }}
      p={1}
      spacing={1}
    >
      <Grid2>{renderEditionButtons()}</Grid2>

      <Grid2>
        <Divider
          orientation="horizontal"
          sx={{ backgroundColor: 'gray', mb: 1 }}
        />
      </Grid2>
      <Grid2 sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {fields && fields.length > 0 ? (
          fields.map((f, idx) => (
            <Grid2
              key={f.key}
              size={12}
              mb={2}
              p={isEditing ? 1 : 2}
              borderRadius={2}
              border="1px solid"
              borderColor="gray"
            >
              {isEditing
                ? renderEditionItemByChannel(idx)
                : renderItemByChannel(f)}
            </Grid2>
          ))
        ) : (
          <Grid2>Nenhum canal registrado</Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default DvrChannelsConfigTab;
